
import { useState } from 'react';

interface FolderPasswordEntry {
  folder_name: string;
  has_password: boolean;
  updated_at: string | null;
  is_protected: boolean;
}

interface PasswordTableProps {
  entries: FolderPasswordEntry[];
  onSetPassword: (folderName: string, password: string) => Promise<void>;
  onRemovePassword: (folderName: string) => Promise<void>;
  loading: boolean;
}

export default function PasswordTable({ entries, onSetPassword, onRemovePassword, loading }: PasswordTableProps) {
  const [editingFolder, setEditingFolder] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const [successFolder, setSuccessFolder] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'protected' | 'unprotected'>('all');
  const [search, setSearch] = useState('');

  const filteredEntries = entries.filter(entry => {
    const matchesSearch = entry.folder_name.toLowerCase().includes(search.toLowerCase());
    if (filter === 'protected') return matchesSearch && entry.is_protected;
    if (filter === 'unprotected') return matchesSearch && !entry.is_protected;
    return matchesSearch;
  });

  const handleSave = async (folderName: string) => {
    if (!newPassword.trim()) return;
    setSaving(true);
    try {
      await onSetPassword(folderName, newPassword);
      setEditingFolder(null);
      setNewPassword('');
      setShowPassword(false);
      setSuccessFolder(folderName);
      setTimeout(() => setSuccessFolder(null), 2500);
    } catch {
      // error handled in parent
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (folderName: string) => {
    setRemoving(true);
    try {
      await onRemovePassword(folderName);
      setConfirmRemove(null);
      setSuccessFolder(folderName);
      setTimeout(() => setSuccessFolder(null), 2500);
    } catch {
      // error handled in parent
    } finally {
      setRemoving(false);
    }
  };

  const handleStartEdit = (folderName: string) => {
    setEditingFolder(folderName);
    setNewPassword('');
    setShowPassword(false);
    setConfirmRemove(null);
  };

  const handleCancelEdit = () => {
    setEditingFolder(null);
    setNewPassword('');
    setShowPassword(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin block mb-4"></i>
          <p className="text-gray-500 text-sm">Loading folder data...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Filters & Search */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search folders..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
        </div>
        <div className="flex bg-gray-100 rounded-lg p-1">
          {(['all', 'protected', 'unprotected'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                filter === f
                  ? 'bg-white text-teal-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {f === 'all' ? 'All' : f === 'protected' ? 'Protected' : 'Unprotected'}
            </button>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
          <p className="text-2xl font-bold text-gray-900">{entries.length}</p>
          <p className="text-xs text-gray-500 mt-1">Total Folders</p>
        </div>
        <div className="bg-teal-50 rounded-lg p-4 border border-teal-100">
          <p className="text-2xl font-bold text-teal-700">{entries.filter(e => e.is_protected && e.has_password).length}</p>
          <p className="text-xs text-teal-600 mt-1">Password Set</p>
        </div>
        <div className="bg-amber-50 rounded-lg p-4 border border-amber-100">
          <p className="text-2xl font-bold text-amber-700">{entries.filter(e => e.is_protected && !e.has_password).length}</p>
          <p className="text-xs text-amber-600 mt-1">Missing Password</p>
        </div>
      </div>

      {/* Table */}
      {filteredEntries.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-100">
          <i className="ri-folder-unknow-line text-4xl text-gray-300 block mb-3"></i>
          <p className="text-gray-500 text-sm">No folders match your search</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredEntries.map((entry) => (
            <div
              key={entry.folder_name}
              className={`bg-white rounded-xl border transition-all duration-200 ${
                successFolder === entry.folder_name
                  ? 'border-green-300 bg-green-50'
                  : editingFolder === entry.folder_name
                  ? 'border-teal-300 shadow-md'
                  : 'border-gray-100 hover:border-gray-200 hover:shadow-sm'
              }`}
            >
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      entry.is_protected
                        ? entry.has_password
                          ? 'bg-teal-100'
                          : 'bg-amber-100'
                        : 'bg-gray-100'
                    }`}>
                      <i className={`text-lg ${
                        entry.is_protected
                          ? entry.has_password
                            ? 'ri-lock-line text-teal-600'
                            : 'ri-lock-unlock-line text-amber-600'
                          : 'ri-folder-line text-gray-400'
                      }`}></i>
                    </div>
                    <div className="min-w-0">
                      <h4 className="font-semibold text-gray-900 text-sm truncate">{entry.folder_name}</h4>
                      <div className="flex items-center gap-2 mt-0.5">
                        {entry.is_protected ? (
                          entry.has_password ? (
                            <span className="inline-flex items-center gap-1 text-xs text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full">
                              <i className="ri-shield-check-line text-xs"></i>
                              Password set
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                              <i className="ri-alert-line text-xs"></i>
                              No password set
                            </span>
                          )
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full">
                            <i className="ri-lock-unlock-line text-xs"></i>
                            Public folder
                          </span>
                        )}
                        {entry.updated_at && (
                          <span className="text-xs text-gray-400">
                            Updated {new Date(entry.updated_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Success indicator */}
                  {successFolder === entry.folder_name ? (
                    <div className="flex items-center gap-1.5 text-green-600">
                      <i className="ri-checkbox-circle-fill text-lg"></i>
                      <span className="text-sm font-medium whitespace-nowrap">Saved!</span>
                    </div>
                  ) : editingFolder === entry.folder_name ? null : (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleStartEdit(entry.folder_name)}
                        className="px-3 py-1.5 text-sm font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {entry.has_password ? 'Change' : 'Set Password'}
                      </button>
                      {entry.has_password && (
                        <button
                          onClick={() => setConfirmRemove(entry.folder_name)}
                          className="w-8 h-8 flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                          title="Remove password"
                        >
                          <i className="ri-delete-bin-line text-base"></i>
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Edit Form */}
                {editingFolder === entry.folder_name && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <label className="block text-xs font-medium text-gray-500 mb-2">
                      {entry.has_password ? 'New Password' : 'Set Password'}
                    </label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="Enter password..."
                          className="w-full px-3 py-2.5 pr-10 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && newPassword.trim()) {
                              handleSave(entry.folder_name);
                            }
                            if (e.key === 'Escape') {
                              handleCancelEdit();
                            }
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                        >
                          <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-base`}></i>
                        </button>
                      </div>
                      <button
                        onClick={() => handleSave(entry.folder_name)}
                        disabled={!newPassword.trim() || saving}
                        className="px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                      >
                        {saving ? (
                          <i className="ri-loader-4-line animate-spin text-base"></i>
                        ) : (
                          <i className="ri-save-line text-base"></i>
                        )}
                        Save
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="px-3 py-2.5 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Remove Confirmation */}
                {confirmRemove === entry.folder_name && (
                  <div className="mt-4 pt-4 border-t border-red-100 bg-red-50 -mx-4 -mb-4 px-4 pb-4 rounded-b-xl">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-red-700">
                        <i className="ri-alert-line mr-1"></i>
                        Remove password from this folder?
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setConfirmRemove(null)}
                          disabled={removing}
                          className="px-3 py-1.5 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleRemove(entry.folder_name)}
                          disabled={removing}
                          className="px-3 py-1.5 text-sm text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center gap-1"
                        >
                          {removing ? (
                            <i className="ri-loader-4-line animate-spin"></i>
                          ) : (
                            <i className="ri-delete-bin-line"></i>
                          )}
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
